import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";

const cta = "mt-6 flex h-14 w-full items-center justify-center rounded-xl bg-slate-900 text-lg font-semibold text-white disabled:opacity-50";

export const State = ({ status, title, sub, children }) => {
  const Icon = { loading: Loader2, done: CheckCircle2, error: XCircle }[status];
  const tone = { loading: "animate-spin text-slate-400", done: "text-emerald-600", error: "text-red-500" }[status];
  return (
    <AuthShell title={title} sub={sub} footer={null}>
      <div data-testid="subscribe-page" data-status={status}>
        <Icon className={`h-10 w-10 ${tone}`} />
        {children}
      </div>
    </AuthShell>
  );
};

const AlreadySubscribed = ({ company }) => (
  <State status="done" title="You're already subscribed" sub={`${company ? `${company} is` : "Your account is"} all set. Your quote link stays live, nothing else to do.`}>
    <Link to="/login" className={cta} data-testid="subscribe-login-link">Go to your dashboard</Link>
  </State>
);

export const Failed = ({ message }) => (
  <State status="error" title="Link not available" sub={message}>
    <Link to="/login" className={cta} data-testid="subscribe-login-link">Log in to subscribe</Link>
  </State>
);

export function SubscribeStart() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const canceled = params.get("canceled") === "1";
  const [state, setState] = useState({ status: canceled ? "canceled" : "loading" });
  const started = useRef(false);

  const start = () => {
    setState({ status: "loading" });
    api.post("/public/subscribe", { token })
      .then(({ data }) => (data.status === "checkout" ? window.location.assign(data.checkout_url) : setState({ status: "already", company: data.company_name })))
      .catch((e) => setState({ status: "error", message: errorMessage(e) }));
  };
  useEffect(() => { if (!canceled && !started.current) { started.current = true; start(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "already") return <AlreadySubscribed company={state.company} />;
  if (state.status === "error") return <Failed message={state.message} />;
  if (state.status === "canceled") return (
    <State status="error" title="Checkout canceled" sub="No charge was made. You can try again whenever you're ready.">
      <button type="button" onClick={start} className={cta} data-testid="subscribe-retry-button">Subscribe · $99/month</button>
    </State>
  );
  return <State status="loading" title="Opening secure checkout..." sub="Taking you to Stripe to finish your subscription." />;
}

export function SubscribeSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let tries = 0;
    let timer;
    const check = () => {
      tries += 1;
      api.post(`/public/subscribe/status?session_id=${encodeURIComponent(params.get("session_id") || "")}`, { token: params.get("token") || "" })
        .then(({ data }) => {
          if (data.payment_status === "paid") setState({ status: "done", company: data.company_name });
          else if (tries < 10) timer = setTimeout(check, 2000);
          else setState({ status: "pending" });
        })
        .catch((e) => setState({ status: "error", message: errorMessage(e) }));
    };
    check();
    return () => clearTimeout(timer);
  }, [params]);

  if (state.status === "done") return (
    <State status="done" title="You're subscribed" sub={`Thanks${state.company ? `, ${state.company}` : ""}! Your quote link stays live.`}>
      <Link to="/login" className={cta} data-testid="subscribe-login-link">Go to your dashboard</Link>
    </State>
  );
  if (state.status === "error") return <Failed message={state.message} />;
  if (state.status === "pending") return (
    <State status="loading" title="Payment is processing" sub="This can take a minute. Your account updates automatically once Stripe confirms." />
  );
  return <State status="loading" title="Confirming your payment..." sub="One moment." />;
}
